import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import type {
  IntegrationDebugPayload,
  IntegrationRawExchange,
} from "@src/load-agent/utils/integrationDebugStorage";

const preBlockStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  padding: "8px",
  backgroundColor: "#faf9f8",
  border: "1px solid #edebe9",
  borderRadius: 2,
  fontSize: 11,
  lineHeight: 1.35,
  fontFamily: "Consolas, Menlo, Monaco, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 180,
  overflow: "auto",
};
const preMetaStyle: React.CSSProperties = {
  ...preBlockStyle,
  maxHeight: 100,
};
const preRequestBodyStyle: React.CSSProperties = {
  ...preBlockStyle,
  maxHeight: 280,
};
const traceLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "#605e5c",
  margin: "8px 0 0 0",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const traceSectionStyle: React.CSSProperties = {
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid #edebe9",
};
const disclosureListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 8,
};
const disclosureShellStyle: React.CSSProperties = {
  border: "1px solid #edebe9",
  borderRadius: 4,
  overflow: "hidden",
  backgroundColor: "#fff",
};
const disclosureHeaderBtnStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  margin: 0,
  border: "none",
  backgroundColor: "#f3f2f1",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  color: "#323130",
  textAlign: "left",
  fontFamily: "inherit",
};
const disclosureBodyStyle: React.CSSProperties = {
  padding: "10px 12px 12px",
  borderTop: "1px solid #edebe9",
  backgroundColor: "#faf9f8",
};
const disclosureChevronStyle = (expanded: boolean): React.CSSProperties => ({
  display: "inline-block",
  width: 10,
  fontSize: 10,
  color: "#605e5c",
  transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
  transition: "transform 0.12s ease",
});
const disclosureBadgeStyle: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: 11,
  fontWeight: 400,
  color: "#605e5c",
};
const nestedDisclosureHeaderStyle: React.CSSProperties = {
  ...disclosureHeaderBtnStyle,
  backgroundColor: "#faf9f8",
  fontSize: 12,
  padding: "8px 10px",
};
const nestedDisclosureShellStyle: React.CSSProperties = {
  ...disclosureShellStyle,
  marginTop: 8,
};
const sectionTitleStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#323130",
  margin: "0 0 6px 0",
};
const mutedStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#605e5c",
  margin: 0,
};

type TraceSectionId = "tms" | "greenscreens" | "sonar";

function exchangeHasBody(ex?: IntegrationRawExchange | null): boolean {
  return !!(
    ex?.responseRaw?.trim() ||
    ex?.requestBodyPrepared?.trim() ||
    ex?.requestMeta?.trim() ||
    ex?.requestRaw?.trim()
  );
}

function splitLegacyRequestRaw(requestRaw: string): {
  meta: string;
  body: string;
} {
  const idx = requestRaw.indexOf("\n\n");

  if (idx === -1) return { meta: "", body: requestRaw };
  return {
    meta: requestRaw.slice(0, idx).trim(),
    body: requestRaw.slice(idx + 2).trim(),
  };
}

function formatJsonBodyForDisplay(bodyText: string): string {
  if (!bodyText.trim()) return bodyText;
  try {
    return JSON.stringify(JSON.parse(bodyText), null, 2);
  } catch {
    return bodyText;
  }
}

function resolvedIntegrationRequestParts(exchange: IntegrationRawExchange | null | undefined): {
  meta: string;
  body: string;
} {
  const prepared = exchange?.requestBodyPrepared?.trim();
  const meta = exchange?.requestMeta?.trim();

  if (prepared || meta) {
    return {
      meta: meta ?? "",
      body:
        prepared ??
        formatJsonBodyForDisplay(splitLegacyRequestRaw(exchange?.requestRaw ?? "").body),
    };
  }
  const legacy = splitLegacyRequestRaw(exchange?.requestRaw ?? "");

  return {
    meta: legacy.meta,
    body: formatJsonBodyForDisplay(legacy.body),
  };
}

function RawExchangeBody({
  exchange,
  emptyHint,
}: {
  exchange: IntegrationRawExchange | null | undefined;
  emptyHint: string;
}): React.ReactElement {
  if (!exchangeHasBody(exchange)) {
    return <p style={mutedStyle}>{emptyHint}</p>;
  }
  const { meta, body } = resolvedIntegrationRequestParts(exchange);
  const hasRequest = !!(meta || body.trim());

  return (
    <div>
      {hasRequest ? (
        <>
          {meta ? (
            <>
              <div style={traceLabelStyle}>Request</div>
              <pre style={preMetaStyle}>{meta}</pre>
            </>
          ) : null}
          <div style={traceLabelStyle}>Request body (as sent)</div>
          <pre style={preRequestBodyStyle}>{body.trim() ? body : "(empty)"}</pre>
        </>
      ) : null}
      <div style={traceLabelStyle}>Response</div>
      <pre style={preBlockStyle}>{exchange?.responseRaw || "(empty)"}</pre>
    </div>
  );
}

function TmsNestedCalls({
  tmsCalls,
  openTmsCall,
  onToggleTmsCall,
}: {
  tmsCalls: IntegrationRawExchange[];
  openTmsCall: number | null;
  onToggleTmsCall: (index: number) => void;
}): React.ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {tmsCalls.map((ex, i) => {
        const callExpanded = openTmsCall === i;
        const callHas = exchangeHasBody(ex);

        return (
          <div key={i} style={i === 0 ? {} : { marginTop: 8 }}>
            <div style={nestedDisclosureShellStyle}>
              <button
                type="button"
                style={nestedDisclosureHeaderStyle}
                aria-expanded={callExpanded}
                onClick={() => onToggleTmsCall(i)}
              >
                <span style={disclosureChevronStyle(callExpanded)} aria-hidden>
                  ▶
                </span>
                TMS call {i + 1}
                <span style={disclosureBadgeStyle}>{callHas ? "HTTP exchange" : "Empty"}</span>
              </button>
              {callExpanded ? (
                <div style={disclosureBodyStyle}>
                  <RawExchangeBody
                    exchange={ex}
                    emptyHint="This call had no captured request/response body."
                  />
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SonarNestedCalls({
  sonarCalls,
  openSonarCall,
  onToggleSonarCall,
}: {
  sonarCalls: IntegrationRawExchange[];
  openSonarCall: number | null;
  onToggleSonarCall: (index: number) => void;
}): React.ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {sonarCalls.map((ex, i) => {
        const callExpanded = openSonarCall === i;
        const callHas = exchangeHasBody(ex);

        return (
          <div key={i} style={i === 0 ? {} : { marginTop: 8 }}>
            <div style={nestedDisclosureShellStyle}>
              <button
                type="button"
                style={nestedDisclosureHeaderStyle}
                aria-expanded={callExpanded}
                onClick={() => onToggleSonarCall(i)}
              >
                <span style={disclosureChevronStyle(callExpanded)} aria-hidden>
                  ▶
                </span>
                Sonar call {i + 1}
                <span style={disclosureBadgeStyle}>{callHas ? "HTTP exchange" : "Empty"}</span>
              </button>
              {callExpanded ? (
                <div style={disclosureBodyStyle}>
                  <RawExchangeBody
                    exchange={ex}
                    emptyHint="This call had no captured request/response body."
                  />
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TraceDisclosureSection({
  expanded,
  onToggle,
  label,
  badgeText,
  children,
}: {
  expanded: boolean;
  onToggle: () => void;
  label: string;
  badgeText: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div style={disclosureShellStyle}>
      <button
        type="button"
        style={disclosureHeaderBtnStyle}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span style={disclosureChevronStyle(expanded)} aria-hidden>
          ▶
        </span>
        {label}
        <span style={disclosureBadgeStyle}>{badgeText}</span>
      </button>
      {expanded ? <div style={disclosureBodyStyle}>{children}</div> : null}
    </div>
  );
}

function TraceAccordionIntro({ hasAny }: { hasAny: boolean }): React.ReactElement {
  return (
    <>
      <h3 style={sectionTitleStyle}>Last vendor HTTP trace</h3>
      <p style={mutedStyle}>
        From the most recent <strong>Get rates</strong> run (overwrites each time).{" "}
        <strong>Request body</strong> is the JSON actually sent after server preparation (e.g. 3PL
        GlobalRating normalization). Open one section at a time. Secrets are redacted; responses may
        still contain sensitive data—use only for local debugging.
      </p>
      {!hasAny ? (
        <p style={mutedStyle}>
          Nothing captured yet. After you fetch rates, open this dialog again; then expand TMS or a
          rating engine below.
        </p>
      ) : null}
    </>
  );
}

type IntegrationTraceDisclosuresProps = {
  stored: IntegrationDebugPayload | null;
  openSection: TraceSectionId | null;
  toggleSection: (id: TraceSectionId) => void;
  showRatingEngineTraces: boolean;
  hasTms: boolean;
  tmsCalls: IntegrationRawExchange[];
  hasGs: boolean;
  hasSonar: boolean;
  sonarCalls: number;
  tmsSectionTitle: string;
  openTmsCall: number | null;
  toggleTmsCall: (i: number) => void;
  openSonarCall: number | null;
  toggleSonarCall: (i: number) => void;
};

function IntegrationTraceDisclosures({
  stored,
  openSection,
  toggleSection,
  showRatingEngineTraces,
  hasTms,
  tmsCalls,
  hasGs,
  hasSonar,
  sonarCalls,
  tmsSectionTitle,
  openTmsCall,
  toggleTmsCall,
  openSonarCall,
  toggleSonarCall,
}: IntegrationTraceDisclosuresProps): React.ReactElement {
  return (
    <div style={disclosureListStyle}>
      <TraceDisclosureSection
        expanded={openSection === "tms"}
        onToggle={() => toggleSection("tms")}
        label={tmsSectionTitle}
        badgeText={hasTms ? `${tmsCalls.length} call(s)` : "No data yet"}
      >
        {!hasTms ? (
          <p style={mutedStyle}>
            No TMS HTTP trace yet. Run <strong>Get rates</strong> or <strong>Book</strong> to
            capture TMS exchanges.
          </p>
        ) : (
          <TmsNestedCalls
            tmsCalls={tmsCalls}
            openTmsCall={openTmsCall}
            onToggleTmsCall={toggleTmsCall}
          />
        )}
      </TraceDisclosureSection>
      {showRatingEngineTraces ? (
        <TraceDisclosureSection
          expanded={openSection === "greenscreens"}
          onToggle={() => toggleSection("greenscreens")}
          label="GreenScreens (rating engine)"
          badgeText={hasGs ? "Has trace" : "No data yet"}
        >
          <RawExchangeBody
            exchange={stored?.greenscreens}
            emptyHint="No GreenScreens HTTP trace for the last quote (LTL quotes skip FTL rating engines)."
          />
        </TraceDisclosureSection>
      ) : null}
      {showRatingEngineTraces ? (
        <TraceDisclosureSection
          expanded={openSection === "sonar"}
          onToggle={() => toggleSection("sonar")}
          label="Sonar (rating engine)"
          badgeText={hasSonar ? `${sonarCalls} call(s)` : "No data yet"}
        >
          {!hasSonar ? (
            <p style={mutedStyle}>
              No Sonar HTTP trace yet. Run <strong>Get rates</strong> on an FTL load with Sonar
              enabled.
            </p>
          ) : (
            <SonarNestedCalls
              sonarCalls={stored?.sonar ?? []}
              openSonarCall={openSonarCall}
              onToggleSonarCall={toggleSonarCall}
            />
          )}
        </TraceDisclosureSection>
      ) : null}
    </div>
  );
}

export function IntegrationDebugAccordion({
  stored,
  selectedMode,
}: {
  stored: IntegrationDebugPayload | null;
  selectedMode?: "LTL" | "FTL" | null;
}): React.ReactElement {
  const [openSection, setOpenSection] = useState<TraceSectionId | null>(null);
  const [openSonarCall, setOpenSonarCall] = useState<number | null>(null);
  const [openTmsCall, setOpenTmsCall] = useState<number | null>(null);
  const tmsCalls = stored?.tms ?? [];
  const hasTms = tmsCalls.length > 0;
  const hasGs = exchangeHasBody(stored?.greenscreens);
  const sonarCalls = stored?.sonar?.length ?? 0;
  const hasSonar = sonarCalls > 0;
  const showRatingEngineTraces = selectedMode !== "LTL";
  const toggleSection = useCallback((id: TraceSectionId) => {
    setOpenSection((prev) => (prev === id ? null : id));
  }, []);
  const toggleSonarCall = useCallback((index: number) => {
    setOpenSonarCall((prev) => (prev === index ? null : index));
  }, []);
  const toggleTmsCall = useCallback((index: number) => {
    setOpenTmsCall((prev) => (prev === index ? null : index));
  }, []);

  useEffect(() => {
    if (openSection !== "sonar") {
      setOpenSonarCall(null);
    }
    if (openSection !== "tms") {
      setOpenTmsCall(null);
    }
  }, [openSection]);
  const hasAny = hasTms || (showRatingEngineTraces && (hasGs || hasSonar));
  const tmsSectionTitle = stored?.tmsProviderName?.trim() || "TMS";

  return (
    <div style={traceSectionStyle}>
      <TraceAccordionIntro hasAny={hasAny} />
      <IntegrationTraceDisclosures
        stored={stored}
        openSection={openSection}
        toggleSection={toggleSection}
        showRatingEngineTraces={showRatingEngineTraces}
        hasTms={hasTms}
        tmsCalls={tmsCalls}
        hasGs={hasGs}
        hasSonar={hasSonar}
        sonarCalls={sonarCalls}
        tmsSectionTitle={tmsSectionTitle}
        openTmsCall={openTmsCall}
        toggleTmsCall={toggleTmsCall}
        openSonarCall={openSonarCall}
        toggleSonarCall={toggleSonarCall}
      />
    </div>
  );
}
